import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import usePullToRefresh from '../hooks/usePullToRefresh';
import toast from 'react-hot-toast';
import MetadataForm from '../components/MetadataForm';
import LLMProviderSettings from '../components/LLMProviderSettings';
import LLMUsageDashboard from '../components/LLMUsageDashboard';
import LLMTuningSettings from '../components/LLMTuningSettings';

const formatTimestamp = (value) => {
  if (!value) return 'No activity yet';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'No activity yet';
  return parsed.toLocaleString();
};

const asBoolean = (value, fallback = true) => {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return ['true', '1', 'yes'].includes(value.toLowerCase());
  return Boolean(value);
};

const AdminPage = () => {
  const [stats, setStats] = useState(null);
  const [settings, setSettings] = useState({});
  const [defaultPrompts, setDefaultPrompts] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [saving, setSaving] = useState(false);

  const [accessDenied, setAccessDenied] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, settingsRes] = await Promise.all([
        api.get('/api/admin/dashboard'),
        api.get('/api/admin/settings')
      ]);
      setStats(statsRes.data);
      setSettings(settingsRes.data);
      try {
        const { data } = await api.get('/api/admin/prompts/defaults');
        setDefaultPrompts(data || {});
      } catch {
        setDefaultPrompts({});
      }
    } catch (error) {
      if (error.response && error.response.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  usePullToRefresh(fetchData);

  const handleSaveSettings = async (newSettings) => {
    setSaving(true);
    try {
      await api.post('/api/admin/settings', newSettings);
      setSettings(prev => ({ ...prev, ...newSettings }));
      toast.success('Settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePrompts = async () => {
    setSaving(true);
    try {
      await api.put('/api/admin/prompts', {
        prompt_merge: settings.prompt_merge || null,
        prompt_revision: settings.prompt_revision || null,
        prompt_merge_analysis: settings.prompt_merge_analysis || null,
        prompt_compliance_analysis: settings.prompt_compliance_analysis || null,
      });
      toast.success('Prompts updated');
    } catch {
      toast.error('Failed to update prompts');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPrompt = (key) => {
    setSettings((prev) => ({ ...prev, [key]: '' }));
  };

  const handleCopyDefaultPrompt = (key) => {
    const defaultValue = defaultPrompts[key];
    if (!defaultValue) {
      toast.error('Default prompt not available');
      return;
    }
    setSettings((prev) => ({ ...prev, [key]: defaultValue }));
    toast.success('Default prompt copied');
  };

  const overviewCards = useMemo(() => {
    const usage = stats?.usage || {};
    return [
      { label: 'Users', value: usage.total_users ?? 0 },
      { label: 'Projects', value: usage.total_projects ?? 0 },
      { label: 'Documents', value: usage.total_documents ?? 0 },
      { label: 'Versions', value: usage.total_versions ?? 0 },
      { label: 'Merge Reports', value: usage.total_merge_reports ?? 0 },
      { label: 'Published Policies', value: usage.total_published_policies ?? 0 },
    ];
  }, [stats]);

  const config = stats?.configuration || {};
  const llmTabEnabled = asBoolean(settings.LLM_PROVIDER_UI_ENABLED, true);
  const activity = stats?.activity || {};
  const redirectDomains = Array.isArray(config.allowed_redirect_domains)
    ? config.allowed_redirect_domains
    : [];

  if (loading) return (
    <section className="panel fade-in">
      <div className="panel-head">
        <h1>Admin Console</h1>
      </div>
      <div className="table-skeleton">
        <div className="skeleton skeleton-line skeleton-line--wide" />
        <div className="skeleton skeleton-line" />
      </div>
    </section>
  );

  if (accessDenied) return (
    <section className="panel fade-in" style={{ textAlign: 'center', padding: '3rem' }}>
      <div className="auth-bypass-banner" style={{ marginBottom: '1rem', background: 'var(--danger)' }}>
        Access Denied
      </div>
      <h2>You do not have permission to view this page.</h2>
      <p className="muted">
        Please contact your system administrator or check your account permissions.<br />
        User: {api.defaults.headers.common['X-User-Email'] || 'Unknown'}
      </p>
      <button className="btn btn--primary" style={{ marginTop: '1rem' }} onClick={() => window.location.href = '/'}>
        Return to Home
      </button>
    </section>
  );

  return (
    <section className="panel fade-in">
      <div className="panel-head">
        <p className="eyebrow">Restricted</p>
        <h1>Admin Console</h1>
      </div>

      <div className="project-tabs">
        <button
          className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('dashboard')}
        >
          Dashboard
        </button>
        <button
          className={`tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Practice Profile
        </button>
        <button
          className={`tab-btn ${activeTab === 'prompts' ? 'active' : ''}`}
          onClick={() => setActiveTab('prompts')}
        >
          LLM Prompts
        </button>
        {llmTabEnabled && (
          <button
            className={`tab-btn ${activeTab === 'llm' ? 'active' : ''}`}
            onClick={() => setActiveTab('llm')}
          >
            LLM
          </button>
        )}
        <button
          className="tab-btn"
          onClick={() => window.location.href = '/admin/logs'}
        >
          View Logs
        </button>
      </div>

      {activeTab === 'dashboard' && (
        <div className="admin-dashboard">
          <div className="admin-dashboard__header">
            <h3>Operational Overview</h3>
            <p className="muted">Updated {formatTimestamp(stats?.generated_at)}</p>
          </div>

          <div className="admin-kpi-grid">
            {overviewCards.map((card) => (
              <article key={card.label} className="admin-kpi-card">
                <p className="eyebrow">{card.label}</p>
                <h2>{card.value}</h2>
              </article>
            ))}
          </div>

          <div className="admin-meta-grid">
            <article className="admin-meta-card">
              <h4>Authentication</h4>
              <p><strong>Auth enabled:</strong> {config.auth_enabled ? 'Yes' : 'No'}</p>
              <p><strong>Auth bypass:</strong> {config.auth_bypass ? 'Yes' : 'No'}</p>
              <p><strong>Cookie secure:</strong> {config.cookie_secure ? 'Yes' : 'No'}</p>
              <p><strong>Cookie domain:</strong> {config.cookie_domain || 'Host-only'}</p>
            </article>

            <article className="admin-meta-card">
              <h4>Environment</h4>
              <p><strong>Streaming:</strong> {config.streaming_enabled ? 'Enabled' : 'Disabled'}</p>
              <p><strong>Prompt overrides:</strong> {config.prompt_overrides ?? 0}</p>
            </article>

            <article className="admin-meta-card">
              <h4>Access Control</h4>
              <p><strong>Admin groups:</strong> {(config.admin_groups || []).join(', ') || 'None'}</p>
              <p><strong>Admin emails:</strong> {(config.admin_emails || []).join(', ') || 'None'}</p>
              <p><strong>Redirect domains:</strong> {redirectDomains.join(', ') || 'None'}</p>
              <p><strong>Practice metadata records:</strong> {config.practice_metadata_records ?? 0}</p>
            </article>

            <article className="admin-meta-card">
              <h4>Recent Activity</h4>
              <p><strong>Latest project:</strong> {formatTimestamp(activity.latest_project_created_at)}</p>
              <p><strong>Latest version:</strong> {formatTimestamp(activity.latest_version_created_at)}</p>
              <p><strong>System status:</strong> {stats?.system_status || 'Unknown'}</p>
              <p><strong>Current admin:</strong> {stats?.admin_user || 'Unknown'}</p>
            </article>
          </div>
        </div>
      )}

      {activeTab === 'profile' && (
        <div className="admin-settings">
          <MetadataForm
            isGlobal={true}
            initialData={settings.practice_metadata}
            onSave={(data) => handleSaveSettings({ practice_metadata: data })}
          />
        </div>
      )}

      {activeTab === 'prompts' && (
        <div className="admin-prompts">
          <h2>LLM Prompt Configuration</h2>
          <p className="muted admin-prompts__help">
            Customize merge, revision, and merge analysis behavior.
            Merge placeholders: <code>{'{template_content}'}</code>, <code>{'{practice_a_content}'}</code>, <code>{'{practice_b_content}'}</code>, <code>{'{metadata_str}'}</code>, <code>{'{additional_instructions}'}</code>.
            Analysis placeholders: <code>{'{template_content}'}</code>, <code>{'{practice_a_content}'}</code>, <code>{'{practice_b_content}'}</code>, <code>{'{merged_content}'}</code>, <code>{'{additional_instructions}'}</code>.
            Compliance placeholders: <code>{'{merged_content}'}</code>, <code>{'{additional_instructions}'}</code>, <code>{'{merge_summary}'}</code>, <code>{'{merge_concerns}'}</code>.
          </p>

          <div className="form-group">
            <div className="row-actions">
              <label>Merge Policy Prompt</label>
              <button className="btn btn--sm" onClick={() => handleCopyDefaultPrompt('prompt_merge')}>Copy Default</button>
              <button className="btn btn--sm" onClick={() => handleResetPrompt('prompt_merge')}>Clear Custom</button>
            </div>
            <textarea
              className="policy-display prompt-editor prompt-editor--lg"
              value={settings.prompt_merge || ''}
              onChange={(e) => setSettings({ ...settings, prompt_merge: e.target.value })}
              placeholder="Enter custom prompt for merging..."
            />
          </div>

          <div className="form-group">
            <div className="row-actions">
              <label>Revision Prompt</label>
              <button className="btn btn--sm" onClick={() => handleCopyDefaultPrompt('prompt_revision')}>Copy Default</button>
              <button className="btn btn--sm" onClick={() => handleResetPrompt('prompt_revision')}>Clear Custom</button>
            </div>
            <textarea
              className="policy-display prompt-editor"
              value={settings.prompt_revision || ''}
              onChange={(e) => setSettings({ ...settings, prompt_revision: e.target.value })}
              placeholder="Enter custom prompt for revisions..."
            />
          </div>

          <div className="form-group">
            <div className="row-actions">
              <label>Merge Analysis Prompt</label>
              <button className="btn btn--sm" onClick={() => handleCopyDefaultPrompt('prompt_merge_analysis')}>Copy Default</button>
              <button className="btn btn--sm" onClick={() => handleResetPrompt('prompt_merge_analysis')}>Clear Custom</button>
            </div>
            <textarea
              className="policy-display prompt-editor"
              value={settings.prompt_merge_analysis || ''}
              onChange={(e) => setSettings({ ...settings, prompt_merge_analysis: e.target.value })}
              placeholder="Enter custom prompt for merge analysis..."
            />
          </div>

          <div className="form-group">
            <div className="row-actions">
              <label>Compliance Analysis Prompt</label>
              <button className="btn btn--sm" onClick={() => handleCopyDefaultPrompt('prompt_compliance_analysis')}>Copy Default</button>
              <button className="btn btn--sm" onClick={() => handleResetPrompt('prompt_compliance_analysis')}>Clear Custom</button>
            </div>
            <textarea
              className="policy-display prompt-editor"
              value={settings.prompt_compliance_analysis || ''}
              onChange={(e) => setSettings({ ...settings, prompt_compliance_analysis: e.target.value })}
              placeholder="Enter custom prompt for compliance analysis..."
            />
          </div>

          <div className="form-actions">
            <button
              className="btn btn--primary"
              onClick={handleSavePrompts}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Prompts'}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'llm' && llmTabEnabled && (
        <div className="admin-llm-grid">
          <LLMProviderSettings />
          <LLMTuningSettings
            settings={settings}
            onSave={handleSaveSettings}
            saving={saving}
          />
          <LLMUsageDashboard />
        </div>
      )}
    </section>
  );
};

export default AdminPage;
